import { useState, useEffect } from 'react';
import { buttonStyles } from '@/styles/sharedStyles';

interface SubscriptionData {
  id: number;
  name?: string;
  customerId: number;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  canceledAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ClientSubscriptionProps {
  customerId: number;
  cachedData?: SubscriptionData[] | null;
  onDataLoaded?: (data: SubscriptionData[] | null) => void;
}

export function ClientSubscription({ customerId, cachedData, onDataLoaded }: ClientSubscriptionProps) {
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>(cachedData || []);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (cachedData) {
      setSubscriptions(cachedData);
      setLoading(false);
    } else {
      fetchSubscriptions();
    }
  }, [customerId, cachedData]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const response = await fetch(`/api/customer/subscriptions`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setSubscriptions(data.data || []);
        onDataLoaded?.(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch subscriptions');
        onDataLoaded?.(null);
      }
    } catch (err) {
      setError('Failed to fetch subscriptions');
      onDataLoaded?.(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscription: SubscriptionData) => {
    const confirmCancel = window.confirm(
      'Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period.'
    );
    
    if (!confirmCancel) return;

    try {
      setActionLoading(true);
      const response = await fetch(`/api/customer/subscription/${subscription.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchSubscriptions(); // Refresh subscription data
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async (subscription: SubscriptionData) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/customer/subscription/${subscription.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchSubscriptions(); // Refresh subscription data
      } else {
        setError(data.error || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError('Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Handle timezone properly to avoid off-by-one day errors
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Force UTC interpretation to avoid timezone shifts
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600';
      case 'canceled':
        return 'text-red-600';
      case 'past_due':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      case 'trialing':
        return 'Trial';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const isActiveSubscription = (subscription: SubscriptionData) => {
    // Check for Salesforce Active status (case-insensitive for compatibility)
    return subscription.status === 'Active' || subscription.status.toLowerCase() === 'active';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchSubscriptions}
            className={`${buttonStyles.secondary} px-4 py-2 text-sm`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Subscriptions</h3>
          <p className="text-gray-600 mb-4">
            You don't have any subscriptions. Subscribe to get personalized gift recommendations year-round.
          </p>
          <button
            onClick={() => window.location.href = '/subscription'}
            className={`${buttonStyles.primary} px-6 py-2`}
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {subscriptions.map((subscription) => (
        <div key={subscription.id} className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {subscription.name || `Subscription #${subscription.id}`}
            </h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)} bg-opacity-10`}>
              {getStatusText(subscription.status)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Subscription ID</h4>
              <p className="text-gray-900 font-mono text-sm">
                {subscription.stripeSubscriptionId}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Current Period</h4>
              <p className="text-gray-900">
                {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Next Billing Date</h4>
              <p className="text-gray-900">
                {isActiveSubscription(subscription) && !subscription.canceledAt
                  ? formatDate(subscription.currentPeriodEnd)
                  : 'N/A'}
              </p>
            </div>
          </div>

          {subscription.canceledAt && isActiveSubscription(subscription) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="text-yellow-600 mr-3">⚠️</div>
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">
                    Subscription Scheduled for Cancellation
                  </h4>
                  <p className="text-sm text-yellow-700">
                    Your subscription will end on {formatDate(subscription.currentPeriodEnd)}. 
                    You'll still have access until then.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {isActiveSubscription(subscription) && !subscription.canceledAt && (
              <button
                onClick={() => handleCancelSubscription(subscription)}
                disabled={actionLoading}
                className={`${buttonStyles.secondary} px-4 py-2 text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {actionLoading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            )}

            {subscription.canceledAt && isActiveSubscription(subscription) && (
              <button
                onClick={() => handleReactivateSubscription(subscription)}
                disabled={actionLoading}
                className={`${buttonStyles.primary} px-4 py-2 text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {actionLoading ? 'Reactivating...' : 'Reactivate Subscription'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}